// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnMount,
  useOnChange,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { AsyncUtils } from '../../../../common/utils';
import { AppRoute } from '../../../app';
import { EmptyState, LoadingWrapper, Panel } from '../../../../common/components';
import { selectPendingActions } from '../../../../common/state/pending';
import {
  selectCampaignProducts,
  selectCampaigns,
  selectCurrentPage,
} from '../../state/campaignSelectors';
import {
  fetchCampaigns,
  fetchCampaignProducts,
  createCampaign,
  updateCurrentPage,
} from '../../state/campaignActions';
import CampaignList from './partials/CampaignList';

const useStyles = makeStyles(() => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1',
    height: '100%',
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
}));

function CampaignsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [filterParams, setFilterParams] = useHistoryState('campaignFilterParams', {
    onlyActive: true,
    withActiveSeries: false,
  });
  const [sortParams, setSortParams] = useHistoryState('campaignSortParams', {
    orderBy: 'created',
    sortOrder: 'desc',
  });
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const campaigns = useSelector(selectCampaigns);
  const products = useSelector(selectCampaignProducts);
  const pendingActions = useSelector(selectPendingActions);
  const page = useSelector(selectCurrentPage);
  const history = useHistory();
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!campaigns) {
          setLoading(true);
        }
        await AsyncUtils.executeAll([
          dispatch(fetchCampaigns(page, 20, filterParams)),
          dispatch(fetchCampaignProducts()),
        ]);
      } catch (e) {
        showAlert({
          title: t('campaign.campaignsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }

    handleFetchData();
  });

  const handleCreateCampaign = useCallback(
    async campaignData => {
      try {
        const { id } = await dispatch(createCampaign(campaignData));
        await dispatch(fetchCampaigns(1, 20, filterParams, sortParams));
        ifMounted(() =>
          history.push(AppRoute.CampaignDetails.replace(':campaignId', id), {
            from: history.location,
          })
        );
        showAlert({
          title: t('common.success'),
          message: t('campaign.campaignCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('campaign.campaignCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e;
      }
    },
    [dispatch, history, filterParams, sortParams, ifMounted, showAlert, t]
  );

  const handleSelectCampaign = useCallback(
    params => {
      history.push(AppRoute.CampaignDetails.replace(':campaignId', params.row.id), {
        from: history.location,
      });
    },
    [history]
  );

  const handleFetchCampaigns = useCallback(
    async page => {
      try {
        dispatch(fetchCampaigns(page, 20, filterParams, sortParams));
        dispatch(updateCurrentPage(page));
      } catch (e) {
        showAlert({
          title: t('campaign.campaignsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, filterParams, sortParams, showAlert, t]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        ...filterParams,
        ...params,
      });
    },
    [filterParams, setFilterParams]
  );

  const handleSortChange = useCallback(
    params => {
      setSortParams({
        ...sortParams,
        ...params,
      });
    },
    [sortParams, setSortParams]
  );

  useOnChange(filterParams, () => {
    handleFetchCampaigns(1);
  });

  useOnChange(sortParams, () => {
    handleFetchCampaigns(page);
  });

  return (
    <Box id="Campaigns-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel id="Campaigns-panel" title={t('campaign.campaigns')} className={classes.panel}>
          {!campaigns ? (
            <EmptyState variant="unavailable" message={t('campaign.campaignsNotAvailable')} />
          ) : (
            <CampaignList
              data={campaigns}
              products={_.defaultTo(products, [])}
              onCreateCampaign={handleCreateCampaign}
              onSelectCampaign={handleSelectCampaign}
              onPageChange={handleFetchCampaigns}
              page={page}
              actionsDisabled={!_.isEmpty(pendingActions)}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              filterParams={filterParams}
              sortParams={sortParams}
            />
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default CampaignsPage;
